import { makeStyles } from '@mui/styles'
import { Box, Button, Grid, Stack, TextField, Typography } from '@mui/material'
import { ContractType, LegalEntityParty, Terms } from '@types'
import {
  commonNoActionButtonStyle,
  commonProceedButtonStyle
} from 'components/request-action/styles/styles'
import { GlobalInfoContext, initialState } from 'contexts/GlobalStateContext'
import { CreateGxInitialValues } from 'contexts/infer-types/create-initial'
import { Dayjs } from 'dayjs'
import { useFormik } from 'formik'
import { useSocketConnection } from 'hooks/useSocketConnection'
import Item from 'pages/dashboard/sub-sections/items'
import { base64ToPdf, sendPDFCall } from 'pages/dashboard/utils'
import { useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { startLoadingPDF } from 'redux/slices/pdf'
import { PATH_DASHBOARD } from 'routes/paths'
import * as Yup from 'yup'
import { RootState, dispatch, useSelector } from '../../../../redux/store'
import { PRIMARY } from '../../../../theme/palette'
import { AmountAndCurrency } from '../AmountAndCurrency'
import { TermsAndConditionsSelection } from '../autocomplete/DropDowns'
import { ExpiryDate, TimeRow } from '../section'
import { ElementBreak, Heading, SectionBreak } from './shared'
import { convertAmountStringToObject, defineAmountState } from './utils'
import PrivateEntityContactDetails from './PrivateEntityContactDetails'
import { sanitizeInput } from 'components/request-action/sanitzeUnicode'
import { EntityDetailsRow } from '../EntityRow'
import { PointOfContact } from './types/PointOfContact'
//import PaymentInstructions from './PaymentInformationComponent'
const illegalCharacterMessage = 'Unsupported character replaced with acceptable equivalent'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    width: '100%',
    paddingBottom: '2rem',
    justifyContent: 'space-between',
    textAlign: 'right',
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: 20
  }
})

export type InitialValues = CreateGxInitialValues & {
  name: string
  startDate: string
  endDate: string
}

const PaymentPerformanceGuarantee = () => {
  const classes = useStyles()
  const navigate = useNavigate()

  const [, setApplicant] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })
  const [n, setBene] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })
  const [, setIssuer] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })

  const [, setCurrency] = useState('AUD')
  const hasAutoSelectedEntities = useRef(false)
  const [illegalCharInPurposeDescError, setIllegalCharInPurposeDescError] = useState(false)
  const [illegalCharInOptionalCommentsError, setIllegalCharInOptionalCommentsError] =
    useState(false)
  const [illegalCharInContractNumberError, setIllegalCharContractNumberError] = useState(false)
  const [illegalCharInContractNameError, setIllegalCharContractNameError] = useState(false)
  const [illegalCharInReference, setIllegalCharInReference] = useState(false)
  const [startDateValue, setstartDateValue] = useState<Dayjs | null>(null)
  const [endDateValue, setendDateValue] = useState<Dayjs | null>(null)
  const [contractExpiryValue, setContractExpiryValue] = useState<Dayjs | null>(null)
  const [isTermsSelected, setIsTermsSelected] = useState(false)
  const [isTermsClicked, setIsTermsClicked] = useState(false)

  const { isLoading: isTermsPdfLoading } = useSelector((state: RootState) => state.pdfReducer)
  const {
    setReqInfo,
    purpose,
    applicants,
    beneficiaries,
    issuers,
    reference,
    expiryDate,
    isOpenEnded,
    type,
    amount: contextAmount,
    terms: contextTerms
  } = useContext(GlobalInfoContext)
  const initiatedBy = location.pathname.includes('applicant') ? 'applicant' : 'beneficiary'

  const [amountSet, setAmount] = useState(defineAmountState(contextAmount))
  const [terms, setTerms] = useState<Terms>(contextTerms)

  useEffect(() => {
    setTerms(contextTerms)
  }, [contextTerms?.termsDisplay])

  const { onSocketSend } = useSocketConnection()
  const [, setStartYear] = useState('')
  const [, setStartMonth] = useState('')
  const [, setStartDay] = useState('')
  const [, setContractExpiryYear] = useState('')
  const [, setContractExpiryMonth] = useState('')
  const [, setContractExpiryDay] = useState('')
  const { businessUnitActionContexts: buacs } = useSelector(
    (state: RootState) => state.createGuaranteePayload
  )
  const digitalBuacs = buacs.filter((b) => b.contractType === ContractType.TriContract)
  const { issuers: issuersInReal } = useSelector((root: RootState) => root.termsReducer)

  useEffect(() => {
    if (hasAutoSelectedEntities.current) return

    if (
      initiatedBy === 'applicant' &&
      Array.isArray(digitalBuacs) &&
      digitalBuacs.length === 1 &&
      digitalBuacs[0]?.businessUnitActionContext?.[0]?.legalEntity
    ) {
      const applicantEntity = digitalBuacs[0].businessUnitActionContext[0].legalEntity
      handleEntityValue(applicantEntity, 'applicants')
    }

    if (
      initiatedBy === 'applicant' &&
      Array.isArray(issuersInReal) &&
      issuersInReal?.length === 1 &&
      issuersInReal?.[0]?.issuerLegalEntity
    ) {
      const issuerEntity = issuersInReal?.[0].issuerLegalEntity
      handleEntityValue(issuerEntity, 'issuers')
    }

    hasAutoSelectedEntities.current = true // ✅ run only once
  }, [digitalBuacs, issuersInReal, initiatedBy])

  const pointOfContactSchema = Yup.object().shape({
    position: Yup.string().notRequired(),
    person: Yup.object().shape({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      phone: Yup.string().required('Phone is required'),
      email: Yup.string().email('Invalid email').required('Email is required')
    })
  })

  const GuaranteeCreateSchema = Yup.object()
    .shape({
      applicants: Yup.string().required('At least one Applicant is required'),

      beneficiaries: Yup.array()
        .of(
          Yup.object().shape({
            commonName: Yup.string().required('Beneficiary name is required'),
            onboarded: Yup.boolean().notRequired(),
            designatedPointOfContact: Yup.mixed().when('onboarded', {
              is: false,
              then: pointOfContactSchema.required('Point of contact is required'),
              otherwise: Yup.mixed().nullable(true)
            })
          })
        )
        .min(1, 'At least one Beneficiary is required'),

      issuers: Yup.string().when([], {
        is: () => location.pathname.includes('beneficiary'),
        then: Yup.string().notRequired(),
        otherwise: Yup.string().required('Issuer Name is required')
      }),

      purposeDescription: Yup.string()
        .trim()
        .min(3)
        .required('Purpose of the guarantee is required'),

      amount: Yup.string().min(1).required('Amount cannot be empty'),

      number: Yup.string().min(3).notRequired(),
      name: Yup.string().min(3).notRequired(),
      comments: Yup.string().trim().min(3).notRequired(),

      startDate: Yup.date().notRequired(),
      endDate: Yup.date().notRequired(),

      terms: Yup.string().required('Please select terms'),

      isOpenEnded: Yup.boolean().notRequired(),

      expiryDate: Yup.date().when('isOpenEnded', {
        is: false,
        then: Yup.date().required('Expiry Date is required'),
        otherwise: Yup.date().notRequired()
      }),

      reference: Yup.string().notRequired()
    })
    .test('bene-app-test', 'Applicant and Beneficiary cannot be the same', function (values) {
      const applicant = values?.applicants?.toLowerCase()?.trim?.()
      const beneficiary = values?.beneficiaries?.[0]?.commonName?.toLowerCase()?.trim?.()

      if (applicant && beneficiary && applicant === beneficiary) {
        return this.createError({
          path: 'beneficiaries',
          message: 'Applicant and Beneficiary cannot be the same'
        })
      }

      return true
    })
    .test('bene-issuer-test', 'Beneficiary and Issuer cannot be the same', function (values) {
      const issuer = values?.issuers?.toLowerCase()?.trim?.()
      const beneficiary = values?.beneficiaries?.[0]?.commonName?.toLowerCase()?.trim?.()

      if (issuer && beneficiary && issuer === beneficiary) {
        return this.createError({
          path: 'issuers',
          message: 'Beneficiary and Issuer cannot be the same'
        })
      }

      return true
    })

  const [, setendDateYear] = useState('')
  const [, setendDateMonth] = useState('')
  const [, setendDateDay] = useState('')

  const formik = useFormik<InitialValues>({
    initialValues: {
      applicants: applicants?.[0]?.commonName || '',
      beneficiaries: [
        {
          legalEntityIdentifier: beneficiaries?.[0]?.legalEntityIdentifier || '',
          commonName: beneficiaries[0].commonName || '',
          onboarded: beneficiaries[0]?.onboarded || false,
          designatedPointOfContact: beneficiaries?.[0]?.designatedPointOfContact
            ? {
                position: beneficiaries?.[0]?.designatedPointOfContact?.position || '',
                person: {
                  firstName: beneficiaries?.[0]?.designatedPointOfContact?.person?.firstName || '',
                  lastName: beneficiaries?.[0]?.designatedPointOfContact?.person?.lastName || '',
                  phone: beneficiaries?.[0]?.designatedPointOfContact?.person?.phone || '',
                  email: beneficiaries?.[0]?.designatedPointOfContact?.person?.email || ''
                }
              }
            : undefined
        }
      ],
      issuers: issuers?.[0]?.commonName || '',
      terms: terms.termsDisplay || '',
      purposeDescription: purpose?.purposeDescription || '',
      number: purpose?.contract?.number || '',
      name: purpose?.contract?.name || '',
      comments: purpose?.contract?.comments || '',
      expiryDate: expiryDate || '',
      startDate: purpose?.contract?.startDate || '',
      endDate: purpose?.contract?.endDate || '',
      amount: defineAmountState(contextAmount),
      isOpenEnded: isOpenEnded || false,
      reference: reference || ''
    },
    validationSchema: GuaranteeCreateSchema,
    onSubmit: (values) => {
      setReqInfo({
        reference: values?.reference,
        terms,
        amount: convertAmountStringToObject(amountSet),
        expiryDate: values?.expiryDate,
        isOpenEnded: values?.isOpenEnded,
        purpose: {
          ...purpose,
          purposeDescription: values?.purposeDescription,
          contract: {
            ...purpose?.contract,
            name: values?.name,
            number: values?.number,
            comments: values?.comments
          }
        }
      })
      navigate(PATH_DASHBOARD.general.guaranteeCreate.review)
    }
  })

  const { errors, touched, getFieldProps } = formik

  const handleAmount = (value: string) => {
    formik.setFieldValue('amount', value, true)
    setAmount(value)
  }

  const sanitizeComment = (input: string, type: string) => {
    const { sanitized, mapped, ignored } = sanitizeInput(input)

    const hasIssue = mapped || ignored

    switch (type) {
      case 'purposeDescription':
        setIllegalCharInPurposeDescError(hasIssue)
        break
      case 'comments':
        setIllegalCharInOptionalCommentsError(hasIssue)
        break
      case 'name':
        setIllegalCharContractNameError(hasIssue)
        break
      case 'number':
        setIllegalCharContractNumberError(hasIssue)
        break
      case 'reference':
        setIllegalCharInReference(hasIssue)
        break
    }

    return sanitized
  }
  const childRef = useRef()
  const childRefForTerms = useRef()
  const issuerRef = useRef()
  const handleDate = (value: Dayjs, type: string) => {
    const formattedValue = value?.format('YYYY/MM/DD')

    if (type === 'expiryDate') {
      formik.setFieldValue('expiryDate', formattedValue, true)
      setContractExpiryValue(value)
      setContractExpiryYear(formattedValue?.split('/')[0])
      setContractExpiryMonth(formattedValue?.split('/')[1])
      setContractExpiryDay(formattedValue?.split('/')[2])
    } else if (type === 'startDate') {
      formik.setFieldValue('startDate', formattedValue, true)

      setstartDateValue(value)
      setStartYear(formattedValue?.split('/')[0])
      setStartMonth(formattedValue?.split('/')[1])
      setStartDay(formattedValue?.split('/')[2])
    } else if (type === 'endDate') {
      formik.setFieldValue('endDate', formattedValue, true)

      setendDateValue(value)
      setendDateYear(formattedValue?.split('/')[0])
      setendDateMonth(formattedValue?.split('/')[1])
      setendDateDay(formattedValue?.split('/')[2])
    }
    if (type === 'expiryDate') {
      setReqInfo({
        expiryDate: formattedValue,
        purpose: {
          ...purpose,
          contract: {
            ...purpose?.contract
          }
        }
      })
    } else {
      setReqInfo({
        purpose: {
          ...purpose,
          contract: {
            ...purpose?.contract,
            [`${type}`]: formattedValue
          }
        }
      })
    }
  }
  const handleOpenEndedDate = (value) => {
    setReqInfo({
      isOpenEnded: !isOpenEnded,
      expiryDate: null,
      purpose: {
        ...purpose,
        contract: {
          ...purpose?.contract
        }
      }
    })
    value && formik.setFieldValue('expiryDate', '')
    setContractExpiryValue(null)
    formik.setFieldValue('isOpenEnded', value)
  }

  const handleEntityValue = (value: LegalEntityParty, type: string) => {
    if (typeof value === 'object') {
      if (type === 'applicants') {
        formik.setFieldValue('applicants', value?.commonName, true)
        setApplicant(value)
        setReqInfo({
          applicants: [value]
        })
      } else if (type === 'beneficiaries') {
        const beneficiaryEntry = {
          ...value,
          designatedPointOfContact: undefined
        }
        formik.setFieldValue('beneficiaries', [beneficiaryEntry], true)
        setBene(value)
        setReqInfo({
          beneficiaries: [beneficiaryEntry]
        })
      } else if (type === 'issuers') {
        formik.setFieldValue('issuers', value?.commonName || '', true)
        setIssuer(value)
        setReqInfo({
          issuers: [value]
        })
      }
    }
  }

  const handlePointOfContact = (poc: PointOfContact, type: string) => {
    if (type === 'beneficiaries') {
      const current = formik.values.beneficiaries?.[0] ?? { legalEntityIdentifier: '' }
      const updated = {
        ...current,
        designatedPointOfContact: poc
      }

      formik.setFieldValue('beneficiaries', [updated], true)
      setReqInfo({ beneficiaries: [updated] })
    }
  }

  const handleTerms = (value: Terms) => {
    formik.setFieldValue('terms', value.termsDisplay, true)
    setTerms(value)
    setReqInfo({
      terms: value
    })
  }
  const handleClear = (type: string) => {
    if (type === 'applicants') {
      formik.setFieldValue('applicants', '', true)
      setReqInfo({
        applicants: []
      })
    } else if (type === 'beneficiaries') {
      formik.setFieldValue('beneficiaries', '', true)
      setReqInfo({
        beneficiaries: []
      })
    } else if (type === 'issuers') {
      formik.setFieldValue('issuers', '', true)

      setReqInfo({
        issuers: []
      })
    }
  }

  const exitCreationProcess = () => {
    navigate(PATH_DASHBOARD.general.app)
    setReqInfo(initialState)
  }
  // Usage
  async function executePreviewTerms(documentIdentifier: string, roleIdentifier: string) {
    dispatch(startLoadingPDF())
    await sendPDFCall(documentIdentifier, roleIdentifier, onSocketSend)
  }

  const handleReviewAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors = await formik.validateForm()

    if (Object.keys(errors).length === 0) {
      formik.handleSubmit()
    } else {
      formik.setTouched(
        Object.fromEntries(Object.keys(errors).map((key) => [key, true])) as any,
        true
      )

      const firstErrorField = Object.keys(errors)[0]
      const el = document.querySelector(`[name="${firstErrorField}"]`)
      if (el && 'scrollIntoView' in el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }
  useEffect(() => {
    isTermsClicked && !isTermsPdfLoading && base64ToPdf('terms.pdf', 'pdf')
  }, [isTermsPdfLoading, isTermsClicked])

  const beneficiaryErrors = errors?.beneficiaries?.[0] || {}
  const beneficiaryTouched = touched?.beneficiaries?.[0] || {}
  return (
    <div>
      <form onSubmit={handleReviewAndSubmit}>
        <SectionBreak>
          <Grid container sx={{ marginBottom: '20px' }}>
            <Heading>Parties</Heading>
          </Grid>
          {initiatedBy === 'applicant' ? (
            <div>
              <ElementBreak>
                <EntityDetailsRow
                  label="Applicant Name"
                  type="applicants"
                  touched={touched}
                  errors={errors}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'applicants')}
                  initialOption={undefined} // optional now, we'll fetch from initialValues
                  singleEntry={true}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={digitalBuacs}
                  ref={childRef}
                  resetIssuer={issuerRef}
                />
              </ElementBreak>
              <ElementBreak>
                <EntityDetailsRow
                  label="Beneficiary Name"
                  type="beneficiaries"
                  touched={beneficiaryTouched}
                  errors={beneficiaryErrors}
                  key={`${type}` ? `bene-${type}` : 'bene-section '}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'beneficiaries')}
                  initialOption={beneficiaries[0]}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={[]}
                  resetTermsRef={childRefForTerms}
                  ref={childRef}
                  resetIssuer={issuerRef}
                  setPOC={(value) => value && handlePointOfContact(value, 'beneficiaries')}
                  pocDataFromParent={beneficiaries?.[0]?.designatedPointOfContact}
                  isOnbordedRequired={beneficiaries?.[0]?.onboarded}
                />
              </ElementBreak>
              <ElementBreak>
                {typeof formik.errors.beneficiaries === 'string' && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {formik.errors.beneficiaries}
                  </Typography>
                )}
              </ElementBreak>
            </div>
          ) : null}
          {initiatedBy === 'applicant' && (
            <>
              <ElementBreak>
                <EntityDetailsRow
                  label="Issuer Name"
                  type="issuers"
                  touched={touched}
                  errors={errors}
                  getFieldProps={getFieldProps}
                  key={`${type}` ? `issuer-${type}` : 'issuer-section '}
                  setValue={(value) => handleEntityValue(value, 'issuers')}
                  initialOption={issuers[0]}
                  handleClear={handleClear}
                  singleEntry={issuersInReal?.[0]?.issuerLegalEntity.length === 1}
                  initialValues={issuersInReal}
                  initiatedBy={initiatedBy}
                  resetTermsRef={childRefForTerms}
                  issuerRef={issuerRef}
                  ref={childRef}
                  clearable={false}
                />
              </ElementBreak>
              <ElementBreak>
                {typeof formik.errors.issuers === 'string' && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {formik.errors.issuers}
                  </Typography>
                )}
              </ElementBreak>
            </>
          )}
        </SectionBreak>
        <SectionBreak>
          <Grid container sx={{ marginBottom: '20px' }}>
            <Heading>Guarantee Purpose</Heading>
          </Grid>
          <ElementBreak>
            <Grid container sx={{ marginBottom: '10px' }} spacing={2}>
              <Grid item xs={3.94}>
                <Item>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    label="Contract Number (Optional)"
                    placeholder="Enter Contract Number (Optional)"
                    id="number"
                    name="number"
                    type="text"
                    autoComplete="off"
                    onChange={(e) => {
                      const cleaned = sanitizeComment(e.target.value, 'number')
                      if (formik.values.number !== cleaned) {
                        formik.setFieldValue('number', cleaned)
                      }
                    }}
                    value={formik.values.number || ''}
                    error={Boolean(
                      (touched.number && errors.number) || illegalCharInContractNumberError
                    )}
                    helperText={
                      touched.number && errors.number
                        ? errors.number
                        : illegalCharInContractNumberError
                        ? illegalCharacterMessage
                        : ''
                    }
                    inputProps={{ maxLength: 1000 }}
                  />
                </Item>
              </Grid>
              <Grid item xs={1} />

              <Grid item xs={4.06}>
                <Item>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    label="Contract Name (Optional)"
                    name="name"
                    id="name"
                    type="text"
                    autoComplete="off"
                    placeholder="Enter Contract Name (Optional)"
                    value={formik?.values?.name || ''}
                    onChange={(e) => {
                      const cleaned = sanitizeComment(e.target.value, 'name')
                      if (formik.values?.name !== cleaned) {
                        formik.setFieldValue('name', cleaned)
                      }
                    }}
                    error={Boolean(
                      (touched?.name && errors?.name) || illegalCharInContractNameError
                    )}
                    helperText={
                      touched?.name && errors?.name
                        ? errors?.name
                        : illegalCharInContractNameError
                        ? illegalCharacterMessage
                        : ''
                    }
                    inputProps={{ maxLength: 500 }}
                  />
                </Item>
              </Grid>
            </Grid>
          </ElementBreak>
          <ElementBreak>
            <Grid container spacing={2} sx={{ marginBottom: '10px' }}>
              <TimeRow
                startDateValue={startDateValue}
                endDateValue={endDateValue}
                minDate={startDateValue}
                maxDate={endDateValue}
                setDateValue={handleDate}
                openEnded={formik.values.isOpenEnded}
                setOpenEnded={handleOpenEndedDate}
                errors={errors}
                touched={touched}
                disablePast={false}
              />
            </Grid>
          </ElementBreak>
          <ElementBreak>
            <Grid item xs={9}>
              <Item>
                <TextField
                  id="purpose-of-the-guarantee"
                  multiline
                  rows={10}
                  placeholder="The detailed description of the contract (Mandatory)"
                  label="Purpose of the Guarantee (Mandatory)"
                  value={formik.values.purposeDescription || purpose?.purposeDescription}
                  onChange={(e) => {
                    const cleaned = sanitizeComment(e.target.value, 'purposeDescription')
                    if (formik.values.purposeDescription !== cleaned) {
                      formik.setFieldValue('purposeDescription', cleaned)
                    }
                  }}
                  size="medium"
                  type="text"
                  autoComplete="off"
                  style={{ width: '100%' }}
                  InputLabelProps={{ shrink: true }}
                  error={
                    Boolean(touched.purposeDescription && errors.purposeDescription) ||
                    illegalCharInPurposeDescError
                  }
                  helperText={
                    touched.purposeDescription && errors.purposeDescription
                      ? errors.purposeDescription
                      : illegalCharInPurposeDescError
                      ? illegalCharacterMessage
                      : ''
                  }
                  inputProps={{ maxLength: 1000 }}
                />
              </Item>
            </Grid>
          </ElementBreak>
          {/*<ElementBreak>
            <Grid item xs={9} sx={{ margin: '10px 0 10px 0' }}>
              <TextField
                id="description"
                multiline
                rows={10}
                placeholder="Provide any additional comments here (Optional)"
                label="Optional Comments"
                value={formik.values.comments || ''}
                onChange={(e) => {
                  const cleaned = sanitizeComment(e.target.value, 'comments')
                  if (formik.values.comments !== cleaned) {
                    formik.setFieldValue('comments', cleaned)
                  }
                }}
                size="medium"
                type="text"
                autoComplete="off"
                style={{ width: '100%' }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 1000 }}
                error={Boolean(
                  (touched.comments && errors.comments) || illegalCharInOptionalCommentsError
                )}
                helperText={
                  touched.comments && errors.comments
                    ? touched.comments && errors.comments
                    : illegalCharInOptionalCommentsError
                    ? illegalCharacterMessage
                    : ''
                }
              />
            </Grid>
          </ElementBreak>*/}
        </SectionBreak>
        <SectionBreak>
          <ElementBreak>
            <Heading>Guarantee Details</Heading>
          </ElementBreak>
          <ElementBreak>
            <ExpiryDate
              expiryDateValue={contractExpiryValue}
              setExpiryDate={handleDate}
              openEnded={formik.values.isOpenEnded}
              handleOpenEndedDate={handleOpenEndedDate}
              errors={errors}
              touched={touched}
              data-testid="expiry"
            />
          </ElementBreak>
          <ElementBreak>
            <AmountAndCurrency
              setCurrency={setCurrency}
              amount={amountSet}
              setAmount={handleAmount}
              touched={touched}
              errors={errors}
            />
          </ElementBreak>

          <ElementBreak>
            <Grid item xs={4}>
              <TermsAndConditionsSelection
                setIsTermsSelected={setIsTermsSelected}
                handleEntityValue={handleTerms}
                ref={childRefForTerms}
                errors={errors}
                touched={touched}
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={2} sx={{ marginTop: '10px' }}>
              {isTermsSelected && (
                <Link
                  to="#"
                  style={{
                    color: PRIMARY.main
                  }}
                  onClick={() => {
                    setIsTermsClicked(true)
                    executePreviewTerms(
                      terms.termsIdentifier ?? '',
                      digitalBuacs[0]?.businessUnitActionContext[0]?.workflowRole?.roleIdentifier ||
                        null
                    )
                  }}
                >
                  Preview Terms
                </Link>
              )}
            </Grid>
          </ElementBreak>
        </SectionBreak>
        <SectionBreak>
          <Grid item xs={4.06}>
            <Stack>
              <Grid sx={{ marginBottom: '1rem' }}>
                <Heading>Internal Reference Number</Heading>
              </Grid>
              <TextField
                label="IRN (Optional)"
                sx={{ maxWidth: '32.5%' }}
                type="text"
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                value={formik?.values?.reference}
                onChange={(e) => {
                  const cleaned = sanitizeComment(e.target.value, 'reference')
                  if (formik.values.reference !== cleaned) {
                    formik.setFieldValue('reference', cleaned)
                  }
                }}
                inputProps={{ maxLength: 50 }}
                error={illegalCharInReference}
                helperText={illegalCharInReference ? illegalCharacterMessage : ''}
              />
            </Stack>
          </Grid>
        </SectionBreak>

        <Box>
          <Grid container spacing={2}>
            <Grid container item spacing={1}>
              <Grid item md={1}>
                <Button sx={{ ...commonNoActionButtonStyle }} onClick={exitCreationProcess}>
                  Exit
                </Button>
              </Grid>
              {/* <Grid item md={1} lg={1} /> */}
              <Grid item md={4} lg={4}>
                <Button
                  variant="contained"
                  type="submit"
                  data-testid="review-and-submit-btn"
                  id="review-and-submit-btn"
                  sx={{ ...commonProceedButtonStyle }}
                >
                  Review and Submit
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </form>
    </div>
  )
}

export default PaymentPerformanceGuarantee
